/**
 * Data layer for `igaming-multi-currency-invoice-generation`. Thin typed fetch wrappers over /api/v1/igaming-multi-currency-invoice-generation.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingMultiCurrencyInvoiceGenerationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingMultiCurrencyInvoiceGenerationResponse {
	count: number;
	items: IgamingMultiCurrencyInvoiceGenerationRow[];
}

export interface CreateIgamingMultiCurrencyInvoiceGenerationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-multi-currency-invoice-generation";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingMultiCurrencyInvoiceGenerationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingMultiCurrencyInvoiceGenerationResponse>(res);
}

export async function create(body: CreateIgamingMultiCurrencyInvoiceGenerationBody): Promise<IgamingMultiCurrencyInvoiceGenerationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingMultiCurrencyInvoiceGenerationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingMultiCurrencyInvoiceGenerationQueryKeys = {
	all: ["igaming-multi-currency-invoice-generation"] as const,
	list: () => [...igamingMultiCurrencyInvoiceGenerationQueryKeys.all] as const,
	detail: (id: string) => [...igamingMultiCurrencyInvoiceGenerationQueryKeys.all, id] as const,
};
